import React, { useEffect, useState } from 'react'
import {
	Button,
	List,
	Modal,
	Form,
	Input,
	DatePicker,
	InputNumber,
	Image,
	Row,
	Col,
	Select
} from 'antd'
import { observer } from 'mobx-react-lite'
import { v4 as uuidv4 } from 'uuid'
import { Subtask, Subtasks } from '../../cls/utils/subtasks'
import ProgressInput from './ProgressInput'
import dayjs from 'dayjs'

import { EditOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons'
import { StatusTag } from '../Shared'
import { API_BASE_URL, DO_LOAD } from '../../config'
import UploadImage from '../Shared/UploadImage'
import { nextSortId } from '../../config/utils'
import Title from 'antd/es/typography/Title'
import {
	actLoadSprint,
	newSprint,
	requireSprintById
} from '../../stores/sprints/actions'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { isMobile } from 'react-device-detect'
import { TimedSubtask } from '../../cls/utils/subtasks/extensions/TimedSubtasks'
import { actLoadCompletedSprints } from '../../stores/ui-lst-sprints/actions'
import { useStores } from '../../cls/stores/MobXProvider'
import { useSmartTranslation } from '../../cls/TranslationContext'

const { TextArea } = Input
/**
 * DayPickerInMonth component renders a DatePicker restricted to the given month.
 * It disables all dates outside the provided `currentMonth` range.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {dayjs.Dayjs} props.currentMonth – The month context to limit selectable dates.
 * @param {...any} props – Any other DatePicker props to pass through.
 * @returns {JSX.Element}
 */
const DayPickerInMonth = observer(({ currentMonth, ...props }) => {
	const disabledDate = (current) => {
		const startOfCurrentMonth = currentMonth.startOf('month')
		const endOfCurrentMonth = currentMonth.endOf('month')
		return current < startOfCurrentMonth || current > endOfCurrentMonth
	}

	return (
		<DatePicker
			style={{ minWidth: '150px' }}
			defaultPickerValue={currentMonth}
			disabledDate={disabledDate}
			picker="date"
			{...props}
		/>
	)
})
/**
 * getCustomName returns a Dayjs instance from a UNIX timestamp or ISO string.
 * If the input is a valid UNIX timestamp, it converts via `dayjs.unix()`, otherwise via `dayjs()`.
 *
 * @function
 * @param {string|number} name – A UNIX timestamp or date string.
 * @returns {dayjs.Dayjs} Parsed Dayjs object.
 */
const getCustomName = (name) => {
	const customName = dayjs.unix(name)
	return customName.isValid() ? customName : dayjs(name)
}
/**
 * EditSubtask component displays a modal form for creating or editing a subtask.
 * It handles text, date, number, file uploads, image previews, status selection, and related sprint assignment.
 *
 * @component
 * @param {Object} props.subtaskType – Type of subtask ('timed' or 'base') to determine fields.
 * @param {Object} [props.subtask] – Existing subtask data for editing, or undefined for creation.
 * @param {Function} props.onSave – Callback invoked with form values when the user saves.
 * @param {Function} props.onCancel – Callback invoked to close the modal without saving.
 * @param {Function} props.getNextSortId – Function to get the next sortId for new subtasks.
 * @param {dayjs.Dayjs} [props.currentMonth] – Current month context for timed subtasks.
 * @returns {JSX.Element}
 */
const EditSubtask = observer(
	({ subtaskType, subtask, onSave, onCancel, getNextSortId, ...props }) => {
		const [isModalVisible, setIsModalVisible] = useState(true)
		const { t } = useSmartTranslation()
		// Determine custom name based on subtask type
		const customName =
			subtaskType === 'timed'
				? {
						name: subtask?.name
							? getCustomName(subtask?.name)
							: props?.currentMonth
				  }
				: {}

		// Set the initial status based on the subtask type
		const subTaskStatus = subtaskType === 'timed' ? 'draft' : 'active'

		// Choose the appropriate subtask class based on the type
		let CustomSubtaskClass =
			subtaskType === 'timed' ? TimedSubtask : Subtask

		// Create or edit the subtask based on whether one exists
		const editingSubtask =
			subtask ||
			new CustomSubtaskClass({
				...customName,
				hours: 0,
				sprints: [],
				images: [],
				description: '',
				status: subTaskStatus,
				sortId: getNextSortId()
			})

		const [fileList, setFileList] = useState(
			editingSubtask.images.map((image) => ({
				uid: image,
				name: image,
				status: 'done',
				url: `${API_BASE_URL}/images/uploads/${image}`
			})) || []
		)

		// Handle changes to the file list
		/**
		 * handleFileListChange updates the local fileList state when the user uploads or removes images.
		 * It also synchronizes the subtask’s `images` array of IDs.
		 *
		 * @function
		 * @private
		 * @param {Array<Object>} newFileList – The new AntD Upload fileList array.
		 */
		const handleFileListChange = (newFileList) => {
			setFileList(
				newFileList.map((file) => ({
					uid: file.uid,
					name: file.name,
					status: file.status,
					url: file.url
				}))
			)
			editingSubtask.images = newFileList.map((file) => file.uid)
		}

		return (
			<Modal
				title={editingSubtask ? 'Edit Subtask' : 'Add Subtask'}
				open={isModalVisible}
				onCancel={() => {
					setIsModalVisible(false)
					onCancel() // Ensure modal close state is updated in parent
				}}
				footer={null}
				width={!isMobile && '50%'}
			>
				<Form
					labelCol={{ span: 8 }}
					wrapperCol={{ span: 16 }}
					initialValues={editingSubtask || { status: 'active' }}
					onFinish={(values) => {
						const completeValues = values
						completeValues.images =
							fileList.map((file) => file.uid) || []
						onSave(completeValues)
					}}
				>
					<Form.Item
						label={t('single.sort-id')}
						name="sortId"
						rules={[
							{
								required: true,
								message: t('__enter.please%single.sort-id')
							}
						]}
					>
						<InputNumber min={1} />
					</Form.Item>
					<Form.Item
						label={t('single.name')}
						name="name"
						rules={[
							{
								required: true,
								message: t('__enter.please%single.name')
							}
						]}
					>
						{subtaskType === 'timed' ? (
							<DayPickerInMonth
								currentMonth={props?.currentMonth}
							/>
						) : (
							<Input />
						)}
					</Form.Item>
					{subtaskType === 'timed' && (
						<Form.Item
							label={t('_hour.multiple')}
							name="hours"
							rules={[
								{
									required: true,
									message: t('__enter.please%_hour.multiple')
								}
							]}
						>
							<InputNumber min={0} max={24} step={1} />
						</Form.Item>
					)}
					<Form.Item
						label={t('single.description')}
						name="description"
						rules={[
							{
								required: false,
								message: t('__enter.please%single.description')
							}
						]}
					>
						<TextArea rows={4} showCount />
					</Form.Item>
					<Form.Item
						label={t('single.related-link')}
						name="web"
						rules={[
							{
								pattern: new RegExp(
									/^(https?):\/\/([\w.-]+|localhost)(:\d+)?(\/\S*)?$/
								),
								message: t('single.invalid.url')
							}
						]}
					>
						<Input />
					</Form.Item>
					<Form.Item label={t('single.upload-images')}>
						<UploadImage
							fileList={fileList}
							onFileListChange={handleFileListChange}
						/>
					</Form.Item>
					<Form.Item
						label={t('single.status')}
						name="status"
						rules={[
							{
								required: true,
								message: t('__select.please%single.status')
							}
						]}
					>
						<ProgressInput
							type={
								subtaskType === 'timed'
									? 'monthly-report'
									: 'sprint'
							}
						/>
					</Form.Item>
					{subtaskType == 'timed' && (
						<Form.Item name="sprintIds" noStyle>
							<SprintList />
						</Form.Item>
					)}
					<Form.Item>
						<Button type="primary" htmlType="submit">
							{editingSubtask ? t('single.update') : t('__add')}
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		)
	}
)

/**
 * SprintList Component
 * Displays a list of related sprints with options to select and delete.
 *
 * @param {Object} props - The component props
 * @param {Array} props.value - Currently selected sprint IDs
 * @param {Function} props.onChange - Function to call on sprint selection change
 * @param {Function} props.onDelete - Function to call on sprint deletion
 */
const SprintList = observer(({ value, onChange }) => {
	const { t } = useSmartTranslation()
	const { uiLstSprints, users } = useStores()
	const { loggedInUserId } = users
	const { ids } = uiLstSprints

	// Map sprint IDs to an array of options for the Select component
	const sprintOptions = ids.map((id) => {
		const sprint = requireSprintById(id)
		return {
			key: sprint.name,
			label: `${sprint.name} (${sprint.modified})`,
			value: id
		}
	})

	// Handle sprint deletion
	const handleDelete = (sprintId) => {
		onChange(value.filter((id) => id != sprintId))
	}

	// Load completed sprints when the logged-in user ID changes
	useEffect(() => {
		actLoadCompletedSprints().catch(console.error)
	}, [loggedInUserId])

	return (
		<List
			style={{
				width: '100%',
				marginBottom: '5px',
				height: '300px',
				overflowY: 'auto',
				marginTop: 0
			}}
			header={
				<>
					<Title level={5}>{t('__related%_sprint.multiple')}</Title>
					<Row align="middle">
						<Select
							allowClear
							showSearch
							mode="multiple"
							style={{ width: '100%', marginRight: '10px' }}
							onChange={onChange}
							placeholder={t('__select.item%_sprint.multiple')}
							value={value}
							maxTagCount={1}
							maxTagPlaceholder={`+ more`}
							options={sprintOptions}
						/>
					</Row>
				</>
			}
			dataSource={value}
			renderItem={(sprintId) => (
				<List.Item
					actions={[
						<Button
							key={sprintId + 'delete'}
							type="link"
							onClick={() => {
								handleDelete(sprintId)
							}}
						>
							<DeleteOutlined />
						</Button>
					]}
				>
					<SprintDetails id={sprintId} />
				</List.Item>
			)}
		/>
	)
})

/**
 * SprintDetails Component
 * Displays detailed information about a single sprint.
 *
 * @param {Object} props - The component props
 * @param {string} props.id - The ID of the sprint to display
 * @param {number} props.index - The index of the sprint in the list
 */
const SprintDetails = observer(({ id, index }) => {
	const sprint = requireSprintById(id)

	// Fetch sprint data if it hasn't been loaded yet
	useEffect(() => {
		// Fetch only if sprint is empty
		if (sprint.loaded === DO_LOAD) {
			actLoadSprint(id).catch(() => {})
		}
	}, [id])

	return (
		<Row wrap={false}>
			<Col flex="auto">
				<Link to={`sprints/${id}/view`}>
					{index} {sprint.name}
				</Link>

				{sprint?.subtasks?.list.length > 0 && (
					<ul>
						{sprint.subtasks.list.map((subtask, subIndex) => (
							<li key={subIndex}>{subtask.name}</li>
						))}
					</ul>
				)}
			</Col>
		</Row>
	)
})
/**
 * SubtasksInput component manages the UI for listing, adding, editing, deleting, and copying subtasks.
 * It renders the list of subtasks, action buttons, and invokes an EditSubtask modal when needed.
 *
 * @component
 * @param {class} [props.CustomClass=Subtasks] – Class to instantiate for subtask collection logic.
 * @param {string} props.createdBy – User ID for new subtask ownership.
 * @param {Array} props.value – Current array of subtask data.
 * @param {Function} props.onChange – Callback with updated subtask array after edits.
 * @param {boolean} [props.disabled=false] – Disables all edit controls when true.
 * @param {dayjs.Dayjs} [props.currentMonth] – Month context for timed subtasks.
 * @returns {JSX.Element}
 */
const SubtasksInput = observer(
	({ CustomClass = Subtasks, createdBy, value, onChange, ...props }) => {
		const navigate = useNavigate()
		const { disabled } = props
		const subtasks = new CustomClass(value || [])
		const { itmId } = useParams()

		// get the next sort ID for new subtasks
		/**
		 * getNextSortId computes the next available sortId for a new subtask.
		 *
		 * @function
		 * @private
		 * @returns {number} Next sortId.
		 */
		const getNextSortId = () => {
			return nextSortId(value)
		}

		const subtasksType = subtasks.subtaskType // 'base' or 'timed'
		const subtasksIsTimed = subtasksType === 'timed'
		const { t } = useSmartTranslation()
		const [isModalVisible, setIsModalVisible] = useState(false)
		const [editingSubtask, setEditingSubtask] = useState(null)
		/**
		 * showModal toggles the EditSubtask modal into view for adding a new subtask.
		 *
		 * @function
		 * @private
		 */
		const showModal = () => {
			setEditingSubtask(null)
			setIsModalVisible(true)
		}
		/**
		 * save invokes `onChange` with the current subtask list prepared for saving.
		 *
		 * @function
		 * @private
		 */
		const save = () => {
			onChange(subtasks.prepareForSave())
		}

		// Function to handle saving the edited subtask and creating new one
		/**
		 * onSaveSubtask handles saving or updating a subtask from the modal form.
		 * - If editing, it mutates the existing subtask instance.
		 * - If new, it adds via `subtasks.addSubtask()`.
		 * After changes, it calls `save()` and closes the modal.
		 *
		 * @function
		 * @private
		 * @param {Object} values – Values returned by the modal form.
		 */
		const onSaveSubtask = (values) => {
			if (editingSubtask) {
				switch (subtasksType) {
					case 'timed':
						editingSubtask.name = values.name.unix()
						editingSubtask.hours = values.hours
						editingSubtask.setSprintsIds(values.sprintIds) // Set the new sprint IDs
						break
					default:
						editingSubtask.name = values.name
						break
				}

				editingSubtask.description = values.description
				editingSubtask.images = values.images
				editingSubtask.status = values.status
				editingSubtask.sortId = values.sortId
				editingSubtask.web = values.web
				subtasks.updateSubtask(editingSubtask)
			} else {
				subtasks.addSubtask(
					subtasksIsTimed ? values.name.unix() : values.name,
					subtasksIsTimed && values.hours,
					subtasksIsTimed && { list: values.sprintIds }, // Sprint IDs for timed subtasks
					values.images,
					values.description,
					values.status,
					values.web,
					values.sortId,
					createdBy
				)
			}
			save()
			setIsModalVisible(false)
		}

		// Function to handle editing a subtask
		/**
		 * handleEdit loads an existing subtask into the modal for editing.
		 *
		 * @function
		 * @private
		 * @param {string} subId – ID of the subtask to edit.
		 */
		const handleEdit = (subId) => {
			let subtask = subtasks.getSubtaskById(subId)

			//  Set the custom name of subtask's name if  it is a timed subtask
			if (subtask && subtasksIsTimed) {
				subtask.setName(getCustomName(subtask?.name))
			}

			setEditingSubtask(subtask)
			setIsModalVisible(true)
		}

		// Function to handle deleting a subtask
		/**
		 * handleDelete removes a subtask from the list and invokes `save()`.
		 *
		 * @function
		 * @private
		 * @param {string} subtaskId – ID of the subtask to delete.
		 */
		const handleDelete = (subtaskId) => {
			subtasks.deleteSubtask(subtaskId)
			save()
		}

		// Function to handle copying a subtask to a new sprint
		/**
		 * handleCopy clones an existing subtask into a new sprint.
		 * It generates a new sprint ID, encodes description/images, and navigates to the new sprint’s edit page.
		 *
		 * @function
		 * @private
		 * @param {string} subtaskId – ID of the subtask to copy.
		 */
		const handleCopy = (subtaskId) => {
			const newId = uuidv4()
			let subtask = subtasks.getSubtaskById(subtaskId)

			if (subtask) {
				const encodedDescription = encodeURIComponent(
					subtask?.description.replace(/\n/g, '%0A')
				)

				newSprint(newId).then(() => {
					navigate(
						`/sprints/${newId}/edit?sprint_name=${
							subtask?.name
						}&sprint_desc=${encodedDescription}&sprint_images=${JSON.stringify(
							subtask?.images
						)}&parent_sprint_id=${itmId}&sprint_related_link=${
							subtask?.web
						}`
					)
				})
			}
		}

		return (
			<>
				<List
					className="we-subtasks-input"
					header={
						!disabled && (
							<Button
								type="dashed"
								onClick={() => showModal(true)}
							>
								{t('__add%_subtask')}
							</Button>
						)
					}
					dataSource={subtasks.sortedList}
					locale={{
						emptyText: t('single.no-data')
					}}
					renderItem={(subtask) => (
						<List.Item
							actions={
								!disabled
									? [
											<div key={subtask.id}>
												<Button
													className="no-print"
													type="link"
													onClick={() =>
														handleEdit(subtask.id)
													}
													icon={<EditOutlined />}
												/>
												<Button
													className="no-print"
													type="link"
													onClick={() =>
														handleDelete(subtask.id)
													}
													icon={<DeleteOutlined />}
												/>
												{CustomClass === Subtasks && (
													<Button
														className="no-print"
														type="link"
														onClick={() =>
															handleCopy(
																subtask.id
															)
														}
														icon={<CopyOutlined />}
													/>
												)}
											</div>
									  ]
									: [
											CustomClass === Subtasks && (
												<Button
													className="no-print"
													key={subtask.id}
													type="link"
													onClick={() =>
														handleCopy(subtask.id)
													}
													icon={<CopyOutlined />}
												/>
											)
									  ]
							}
						>
							<Row wrap={false}>
								<Col flex="auto">
									<StatusTag
										tag={subtask.status}
										style={{ marginRight: 3 }}
										isShort={true}
									/>
									{subtask.sortId} - {subtask.displayName}
									{props?.displaySubtaskDescription &&
										subtask?.description && (
											<div
												style={{
													whiteSpace: 'pre-line',
													fontSize: 12,
													color: '#7d7d7d',
													margin: '5px 0 0 0',
													padding: 0
												}}
											>
												{subtask.description}
											</div>
										)}
									{subtask?.images?.length > 0 && (
										<div>
											{subtask?.images &&
												subtask?.images?.map(
													(imagePath) => (
														<Image
															key={imagePath}
															width={100}
															style={{
																marginTop: '5px'
															}}
															preview={{
																maxScale: 20,
																destroyOnClose: true,
																imageRender:
																	() => (
																		<img
																			width="100%"
																			style={{
																				border: '2px solid #000',
																				borderRadius:
																					'10px',
																				objectFit:
																					'contain',
																				maxHeight:
																					'95vh',
																				maxWidth:
																					'95vh',
																				boxShadow:
																					'0 4px 25px rgba(0, 0, 0, 0.6)'
																			}}
																			src={`${API_BASE_URL}/images/uploads/${imagePath}`}
																		/>
																	),
																toolbarRender:
																	() => null
															}}
															src={`${API_BASE_URL}/images/uploads/${imagePath}`}
														/>
													)
												)}
										</div>
									)}
									{subtask?.sprints?.list?.length > 0 && (
										<div style={{ marginTop: '5px' }}>
											<span>Related Sprints:</span>
											{subtask?.sprints?.list?.map(
												(id, index) => {
													return (
														<div key={id}>
															<SprintDetails
																id={id}
																index={`${
																	index + 1
																})`}
															/>
														</div>
													)
												}
											)}
										</div>
									)}
									{subtask?.web && (
										<div style={{ marginTop: '5px' }}>
											<a
												className="no-print"
												href={subtask?.web}
												target="_blank"
												rel="noreferrer"
											>
												{t('single.open-link')}
											</a>
										</div>
									)}
								</Col>
							</Row>
						</List.Item>
					)}
				/>
				{isModalVisible && (
					<EditSubtask
						subtaskType={subtasksType}
						subtask={editingSubtask}
						getNextSortId={getNextSortId}
						onSave={onSaveSubtask}
						onCancel={() => setIsModalVisible(false)}
						currentMonth={props?.currentMonth}
					/>
				)}
			</>
		)
	}
)

export default SubtasksInput
